import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Search, Filter, Share2, Play, CheckCircle, Circle, LogOut, Plus, Trash2, Upload } from 'lucide-react';
// ADD THESE IMPORTS
import { authService } from './services/authService';
import { videoService } from './services/videoService';
import { db } from './db';

const PhysioVideoApp = () => {
  const [user, setUser] = useState(null);
  const [authError, setAuthError] = useState('');
  const [authDebugEvents, setAuthDebugEvents] = useState([]);
  const isAdmin = user?.role === 'Administrator';
  const [loading, setLoading] = useState(true);
  const [videos, setVideos] = useState([]);
  const [activeView, setActiveView] = useState('all');
  const [favorites, setFavorites] = useState([]);
  const [recentShares, setRecentShares] = useState([]);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [playingVideoId, setPlayingVideoId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(''); // NEW: Debounced search
  const [filters, setFilters] = useState({
    bodyPart: '',
    exerciseType: '',
    equipment: '',
    difficulty: '',
    condition: '',
    withAccessory: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showAddVideo, setShowAddVideo] = useState(false);
  const [newVideo, setNewVideo] = useState({
    url: '',
    videoId: '',
    title: '',
    bodyPart: '',
    exerciseTypes: [],
    equipment: '',
    difficulty: '',
    condition: '',
    otherConditions: [],
    withAccessory: false
  });

  // Body parts and exercise types
  const bodyParts = ['Shoulder', 'Knee', 'Lower Back', 'Neck', 'Hip', 'Ankle', 'Wrist', 'Elbow', 'Leg'];
  const exerciseTypes = ['Strengthening', 'Stretching', 'Mobility', 'Balance', 'Cardio'];
  const equipmentOptions = ['None', 'Resistance Band', 'Dumbbells', 'Exercise Ball', 'Foam Roller', 'Yoga Mat', 'Theraband', 'Weights'];
  const difficultyLevels = ['Beginner', 'Intermediate', 'Advanced'];
  const conditionOptions = [
    'Frozen Shoulder', 'Rotator Cuff Injury', 'ACL Recovery', 'Meniscus Tear',
    'Arthritis', 'Lower Back Pain', 'Sciatica', 'Herniated Disc', 'Plantar Fasciitis',
    'Tennis Elbow', 'Carpal Tunnel', 'Post-Surgery', 'Sprain', 'Strain'
  ];
  const navigationItems = [
    { id: 'all', label: 'All videos' },
    { id: 'favorites', label: 'Favourite videos' },
    { id: 'recent', label: 'Recently shared videos' }
  ];

  useEffect(() => {
    if (!user) return;

    const favs = JSON.parse(localStorage.getItem(`favorites_${user?.email}`)) || [];
    setFavorites(favs);
  }, [user]);

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('recentShares')) || [];
    setRecentShares(stored);
  }, []);

  // PERFORMANCE FIX 1: Debounce search input (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // Wait 300ms after user stops typing

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    let unsubscribe = null;
    let isMounted = true;

    const refreshAuthDebugEvents = () => {
      if (!isMounted) return;
      setAuthDebugEvents(authService.getDebugEvents());
    };

    const syncAuthorizedUser = async (currentUser) => {
      if (!isMounted) return;

      if (currentUser) {
        const authorizationResult = await authService.getAuthorizedUserProfile(currentUser.email);

        if (!isMounted) return;

        if (authorizationResult.ok) {
          const authorizedProfile = authorizationResult.profile;
          setAuthError('');
          setUser({
            ...currentUser,
            email: authorizedProfile.email,
            role: authorizedProfile.role,
            displayName: authorizedProfile.name || currentUser.displayName
          });
          await loadVideos();
        } else {
          setUser(null);
          setAuthError(`Signed in as ${currentUser.email}, but no active authorized user record was found.`);
        }
      } else {
        setUser(null);
      }

      refreshAuthDebugEvents();

      if (isMounted) {
        setLoading(false);
      }
    };

    const initializeAuth = async () => {
      try {
        refreshAuthDebugEvents();
        const redirectResult = await authService.handleRedirectResult();
        refreshAuthDebugEvents();

        if (redirectResult?.user) {
          await syncAuthorizedUser(redirectResult.user);
        }
      } catch (error) {
        if (error?.message !== 'Sign-in cancelled') {
          console.error('Redirect sign-in error:', error);
          if (isMounted) {
            setAuthError('Google sign-in could not be completed. Please try again.');
            setLoading(false);
          }
        }
        refreshAuthDebugEvents();
      }

      if (!isMounted) return;

      unsubscribe = authService.onAuthStateChange(async (currentUser) => {
        await syncAuthorizedUser(currentUser);
      });
    };

    initializeAuth();

    return () => {
      isMounted = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const handleGoogleSignIn = async () => {
    try {
      setAuthError('');
      setLoading(true);
      await authService.signInWithGoogle();
      setAuthDebugEvents(authService.getDebugEvents());
      // loadVideos() will be called by onAuthStateChange
    } catch (error) {
      setLoading(false);
      setAuthDebugEvents(authService.getDebugEvents());
      if (error.message === 'Unauthorized user') {
        setAuthError('Your Google account is not authorized to use this app.');
      } else if (error.message === 'Sign-in cancelled') {
        // User closed the popup, do nothing
      } else {
        console.error('Sign in error:', error);
        setAuthError('Sign in failed. Please try again.');
      }
    }
  };

  const handleSignOut = async () => {
    try {
      await authService.signOut();
      setUser(null);
      setVideos([]);
      setSelectedVideos([]);
      setAuthDebugEvents(authService.getDebugEvents());
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const clearAuthDebugEvents = () => {
    authService.clearDebugEvents();
    setAuthDebugEvents([]);
  };

  const loadVideos = async () => {
    try {
      // ✅ TRY LOCAL FIRST
      if (!navigator.onLine) {
        const localVideos = await db.videos.toArray();

        if (localVideos.length > 0) {
          setVideos(localVideos);
        }
        return;
      }

      // 🔄 THEN SYNC FROM FIREBASE (background refresh)
      const remoteVideos = await videoService.getAllVideos();

      setVideos(remoteVideos);

      await db.videos.clear();
      await db.videos.bulkPut(remoteVideos);

    } catch (error) {
      console.error('Error loading videos:', error);
      alert('Failed to load videos. Please try again.');
    }
  };

  // Extract video ID from URL or validate if already an ID
  const extractVideoId = (input) => {
    // If it's already just an ID (11 characters, alphanumeric with - and _)
    if (/^[a-zA-Z0-9_-]{11}$/.test(input)) {
      return input;
    }

    // Extract from various YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=)([^&\s]+)/,
      /(?:youtu\.be\/)([^&\s]+)/,
      /(?:youtube\.com\/embed\/)([^&\s]+)/,
      /(?:youtube\.com\/v\/)([^&\s]+)/
    ];

    for (const pattern of patterns) {
      const match = input.match(pattern);
      if (match) return match[1];
    }

    return null;
  };

  // Generate YouTube URL from video ID
  const getYouTubeUrl = (videoId) => {
    return `https://www.youtube.com/watch?v=${videoId}`;
  };

  // Generate thumbnail URL from video ID
  const getThumbnailUrl = (videoId) => {
    return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
  };

  const addVideo = async () => {
    if (!newVideo.videoId || !newVideo.title || !newVideo.bodyPart ||
      newVideo.exerciseTypes.length === 0 || !newVideo.equipment ||
      !newVideo.difficulty || !newVideo.condition) {
      alert('Please fill in all required fields');
      return;
    }

    const videoId = extractVideoId(newVideo.videoId);

    if (newVideo.exerciseTypes.length === 0) {
      alert('Please select at least one exercise type');
      return;
    }

    if (!videoId) {
      alert('Invalid YouTube URL or Video ID');
      return;
    }

    try {
      const videoData = {
        ...newVideo
      };

      await videoService.addVideo(videoData, user.email);
      await db.videos.put(videoData); // local update
      await loadVideos();

      setNewVideo({
        videoId: '',
        title: '',
        bodyPart: '',
        exerciseTypes: [],
        equipment: '',
        difficulty: '',
        condition: '',
        otherConditions: [],
        withAccessory: false
      });
      setShowAddVideo(false);
      alert('Video added successfully!');
    } catch (error) {
      console.error('Error adding video:', error);
      alert('Failed to add video. Please try again.');
    }
  };

  const deleteVideo = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;

    try {
      await videoService.deleteVideo(videoId);
      await db.videos.delete(videoId); // local delete  
      await loadVideos();
      setSelectedVideos(selectedVideos.filter(id => id !== videoId));
      alert('Video deleted successfully!');
    } catch (error) {
      console.error('Error deleting video:', error);
      alert('Failed to delete video. Please try again.');
    }
  };

  // PERFORMANCE FIX 2: Use useCallback to prevent recreation
  const toggleVideoSelection = useCallback((videoId) => {
    setSelectedVideos(prev =>
      prev.includes(videoId)
        ? prev.filter(id => id !== videoId)
        : [...prev, videoId]
    );
  }, []);

  const toggleFavorite = (videoId) => {
    setFavorites(prev => {
      let updated;

      if (prev.includes(videoId)) {
        updated = prev.filter(id => id !== videoId);
      } else {
        if (prev.length >= 15) {
          alert('Max 15 favourites allowed');
          return prev;
        }
        updated = [...prev, videoId];
      }

      localStorage.setItem(`favorites_${user.email}`, JSON.stringify(updated));
      return updated;
    });
  };

  // Toggle exercise type in the new video form
  const toggleExerciseType = (type) => {
    setNewVideo(prev => ({
      ...prev,
      exerciseTypes: prev.exerciseTypes.includes(type)
        ? prev.exerciseTypes.filter(t => t !== type)
        : [...prev.exerciseTypes, type]
    }));
  };

  const toggleCondition = (condition) => {
    setNewVideo(prev => ({
      ...prev,
      otherConditions: prev.otherConditions.includes(condition)
        ? prev.otherConditions.filter(c => c !== condition)
        : [...prev.otherConditions, condition]
    }));
  };

  // Handle JSON upload with video_id field
  const handleJsonUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const jsonData = JSON.parse(text);

      // Determine if it's an array or single object
      const videosArray = Array.isArray(jsonData) ? jsonData : [jsonData];

      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      // Process each video
      for (let i = 0; i < videosArray.length; i++) {
        const videoData = videosArray[i];

        // Validate required fields
        if (!videoData.exercise_name || (!videoData.video_url && !videoData.video_id) || !videoData.region) {
          errors.push(`Video ${i + 1}: Missing required fields (exercise_name, video_url/video_id, region)`);
          errorCount++;
          continue;
        }

        // Extract video ID from video_url or use video_id directly
        let videoId;
        if (videoData.video_id) {
          videoId = extractVideoId(videoData.video_id);
        } else if (videoData.video_url) {
          videoId = extractVideoId(videoData.video_url);
        }

        if (!videoId) {
          errors.push(`Video ${i + 1}: Invalid YouTube URL or Video ID`);
          errorCount++;
          continue;
        }

        try {
          const mappedVideo = {
            videoId: videoId,  // Store only the ID
            title: videoData.exercise_name,
            bodyPart: videoData.region,
            exerciseTypes: Array.isArray(videoData.exercise_type)
              ? videoData.exercise_type
              : (videoData.exercise_type ? [videoData.exercise_type] : []),
            equipment: videoData.equipment || 'None',
            difficulty: videoData.difficulty || 'Beginner',
            condition: videoData.condition || '',
            otherConditions: Array.isArray(videoData.other_conditions)
              ? videoData.other_conditions
              : (videoData.other_conditions ? [videoData.other_conditions] : []),
            withAccessory: videoData.equipment && videoData.equipment !== 'None',
            createdAt: new Date().toISOString()
          };

          await videoService.addVideo(mappedVideo, user.email);
          successCount++;
        } catch (error) {
          errors.push(`Video ${i + 1} (${videoData.exercise_name}): ${error.message}`);
          errorCount++;
        }
      }
      await loadVideos();

      // Prepare result message
      let message = '';
      if (successCount > 0) {
        message += `Successfully imported ${successCount} video${successCount > 1 ? 's' : ''}!`;
      }
      if (errorCount > 0) {
        message += `\n\nFailed to import ${errorCount} video${errorCount > 1 ? 's' : ''}:`;
        errors.forEach(err => {
          message += `\n- ${err}`;
        });
      }

      alert(message);
      event.target.value = ''; // Reset file input
      await db.videos.bulkPut(videosArray); // local update
    } catch (error) {
      console.error('Error importing JSON:', error);
      alert('Failed to parse JSON file. Please check the file format.\n\nError: ' + error.message);
    }
  };


  // Generate WhatsApp message with YouTube URL
  const shareToWhatsApp = () => {
    if (selectedVideos.length === 0) {
      alert('Please select at least one video to share');
      return;
    }

    const selectedVideoData = videos.filter(v => selectedVideos.includes(v.id));
    // SAVE RECENT (max 10)
    const updatedRecent = [
      ...selectedVideoData.map(v => v.id),
      ...recentShares
    ]
      .filter((v, i, arr) => arr.indexOf(v) === i) // unique
      .slice(0, 10);

    setRecentShares(updatedRecent);
    localStorage.setItem('recentShares', JSON.stringify(updatedRecent));

    const message = selectedVideoData.map(v =>
      `*${v.title}*\n${getYouTubeUrl(v.videoId)}`  // Generate URL from ID
    ).join('\n\n');

    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const baseVideos = useMemo(() => {
    if (activeView === 'favorites') {
      return videos.filter(video => favorites.includes(video.id));
    }

    if (activeView === 'recent') {
      return recentShares
        .map(sharedId => videos.find(video => video.id === sharedId))
        .filter(Boolean);
    }

    return videos;
  }, [activeView, videos, favorites, recentShares]);

  // PERFORMANCE FIX 3: Use useMemo to cache filtered results
  const filteredVideos = useMemo(() => {
    const searchLower = debouncedSearchTerm.toLowerCase(); // Use debounced value

    return baseVideos
      .filter(video => {
        // Early return for filter checks (faster)
        if (filters.bodyPart && video.bodyPart !== filters.bodyPart) return false;
        if (filters.exerciseType && !video.exerciseTypes?.includes(filters.exerciseType)) return false;
        if (filters.equipment && video.equipment !== filters.equipment) return false;
        if (filters.difficulty && video.difficulty !== filters.difficulty) return false;
        if (filters.withAccessory !== '' && video.withAccessory !== (filters.withAccessory === 'true')) return false;

        if (filters.condition) {
          const matchesCondition = video.condition === filters.condition ||
            video.otherConditions?.includes(filters.condition);
          if (!matchesCondition) return false;
        }

        const searchWords = searchLower.split(/\s+/).filter(Boolean);

        return searchWords.every(word => {
          return (
            String(video.title || '').toLowerCase().includes(word) ||
            String(video.bodyPart || '').toLowerCase().includes(word) ||
            String(video.equipment || '').toLowerCase().includes(word) ||
            String(video.difficulty || '').toLowerCase().includes(word) ||
            String(video.condition || '').toLowerCase().includes(word) ||
            (Array.isArray(video.exerciseTypes) &&
              video.exerciseTypes.some(type =>
                String(type || '').toLowerCase().includes(word)
              )) ||
            (Array.isArray(video.otherConditions) &&
              video.otherConditions.some(cond =>
                String(cond || '').toLowerCase().includes(word)
              ))
          );
        });
      })
      .slice(0, 10);
  }, [baseVideos, debouncedSearchTerm, filters]); // Only recalculate when these change

  const activeViewLabel = navigationItems.find(item => item.id === activeView)?.label || 'All videos';

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={styles.container}>
        <div style={styles.loginContainer}>
          <div style={styles.logoSection}>
            <div style={styles.logo}>
              <Play size={48} strokeWidth={2.5} />
            </div>
            <h1 style={styles.appTitle}>Physio Video Hub</h1>
            <p style={styles.appSubtitle}>Exercise videos for patient care</p>
          </div>

          <div style={styles.loginCard}>
            <h2 style={styles.loginTitle}>Welcome Back</h2>
            <p style={styles.loginDescription}>
              Sign in with your authorized Google account to access the video library
            </p>

            {authError && (
              <p style={styles.authError}>{authError}</p>
            )}

            {authService.isDebugEnabled() && authDebugEvents.length > 0 && (
              <div style={styles.authDebugPanel}>
                <div style={styles.authDebugHeader}>
                  <strong>Auth debug trace</strong>
                  <button onClick={clearAuthDebugEvents} style={styles.authDebugClearButton}>
                    Clear
                  </button>
                </div>
                <pre style={styles.authDebugPre}>
                  {JSON.stringify(authDebugEvents.slice(-8), null, 2)}
                </pre>
              </div>
            )}

            <button onClick={handleGoogleSignIn} style={styles.googleButton}>
              <svg style={styles.googleIcon} viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Sign in with Google
            </button>

            <p style={styles.authNote}>
              Only authorized healthcare professionals can access this app
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerTop}>
          <h1 style={styles.headerTitle}>Physio Videos</h1>
          <button onClick={handleSignOut} style={styles.iconButton}>
            <LogOut size={20} />
          </button>
        </div>
        <p style={styles.headerSubtitle}>
          Welcome, {user.displayName || user.email}
          <span style={styles.videoCount}> • {activeViewLabel}: {filteredVideos.length} of {baseVideos.length} exercises</span>
        </p>
      </div>

      <div style={styles.navigationMenu}>
        {navigationItems.map(item => (
          <button
            key={item.id}
            onClick={() => setActiveView(item.id)}
            style={{
              ...styles.navigationButton,
              ...(activeView === item.id ? styles.navigationButtonActive : {})
            }}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* Search Bar */}
      <div style={styles.searchContainer}>
        <div style={styles.searchBar}>
          <Search size={20} style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search exercises..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              style={styles.clearSearchButton}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          style={{ ...styles.filterButton, ...(showFilters ? styles.filterButtonActive : {}) }}
        >
          <Filter size={20} />
        </button>
      </div>

      {/* Show searching indicator */}
      {searchTerm !== debouncedSearchTerm && (
        <div style={styles.searchingIndicator}>
          Searching...
        </div>
      )}

      {/* Filters */}

      {showFilters && (
        <div style={styles.filtersContainer}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Body Part</label>
            <select
              value={filters.bodyPart}
              onChange={(e) => setFilters({ ...filters, bodyPart: e.target.value })}
              style={styles.select}
            >
              <option value="">All</option>
              {bodyParts.map(part => (
                <option key={part} value={part}>{part}</option>
              ))}
            </select>
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Exercise Type</label>
            <select
              value={filters.exerciseType}
              onChange={(e) => setFilters({ ...filters, exerciseType: e.target.value })}
              style={styles.select}
            >
              <option value="">All</option>
              {exerciseTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Equipment</label>
            <select
              value={filters.equipment}
              onChange={(e) => setFilters({ ...filters, equipment: e.target.value })}
              style={styles.select}
            >
              <option value="">All</option>
              {equipmentOptions.map(eq => (
                <option key={eq} value={eq}>{eq}</option>
              ))}
            </select>
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Difficulty</label>
            <select
              value={filters.difficulty}
              onChange={(e) => setFilters({ ...filters, difficulty: e.target.value })}
              style={styles.select}
            >
              <option value="">All</option>
              {difficultyLevels.map(level => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Condition</label>
            <select
              value={filters.condition}
              onChange={(e) => setFilters({ ...filters, condition: e.target.value })}
              style={styles.select}
            >
              <option value="">All</option>
              {conditionOptions.map(cond => (
                <option key={cond} value={cond}>{cond}</option>
              ))}
            </select>
          </div>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Accessory</label>
            <select
              value={filters.withAccessory}
              onChange={(e) => setFilters({ ...filters, withAccessory: e.target.value })}
              style={styles.select}
            >
              <option value="">All</option>
              <option value="true">With Accessory</option>
              <option value="false">Without Accessory</option>
            </select>
          </div>

          <button
            onClick={() => setFilters({
              bodyPart: '', exerciseType: '', equipment: '',
              difficulty: '', condition: '', withAccessory: ''
            })}
            style={styles.clearButton}
          >
            Clear Filters
          </button>
        </div>
      )}

      {/* Action Buttons - UPDATED with smaller icon-only buttons */}
      <div style={styles.actionBar}>
        {isAdmin && (
          <button onClick={() => setShowAddVideo(true)} style={styles.addButtonSmall} title="Add Video">
            <Plus size={20} />
          </button>
        )}
        {isAdmin && (
          <label style={styles.uploadButton} title="Upload JSON">
            <Upload size={20} />
            <input
              type="file"
              accept=".json"
              onChange={handleJsonUpload}
              style={styles.fileInput}
            />
          </label>
        )}
        <button
          onClick={shareToWhatsApp}
          style={{ ...styles.shareButton, ...(selectedVideos.length === 0 ? styles.shareButtonDisabled : {}) }}
          disabled={selectedVideos.length === 0}
        >
          <Share2 size={20} />
          Share ({selectedVideos.length})
        </button>
      </div>

      {/* Video List - PERFORMANCE FIX 4: Render optimization */}
      <div style={styles.videoList}>
        {filteredVideos.length === 0 ? (
          <div style={styles.emptyState}>
            <p style={styles.emptyText}>
              {activeView === 'favorites'
                ? 'No favourite videos yet'
                : activeView === 'recent'
                  ? 'No recently shared videos yet'
                  : 'No exercises found'}
            </p>
            <p style={styles.emptySubtext}>
              {searchTerm || Object.values(filters).some(f => f)
                ? 'Try adjusting your filters or search term'
                : activeView === 'favorites'
                  ? 'Tap the star on a video to save it here'
                  : activeView === 'recent'
                    ? 'Shared videos will appear here after you send them'
                    : 'No exercises available'}
            </p>
          </div>
        ) : (
          filteredVideos.map(video => (
            <VideoCard
              key={video.id}
              video={video}
              isSelected={selectedVideos.includes(video.id)}
              onToggle={toggleVideoSelection}
              onDelete={deleteVideo}
              getThumbnailUrl={getThumbnailUrl}
              favorites={favorites}
              toggleFavorite={toggleFavorite}
              setPlayingVideoId={setPlayingVideoId}
              isAdmin={isAdmin}
            />
          ))
        )}
      </div>

      {/* Add Video Modal - keeping original for brevity */}
      {showAddVideo && (
        <AddVideoModal
          newVideo={newVideo}
          setNewVideo={setNewVideo}
          bodyParts={bodyParts}
          exerciseTypes={exerciseTypes}
          equipmentOptions={equipmentOptions}
          difficultyLevels={difficultyLevels}
          conditionOptions={conditionOptions}
          toggleExerciseType={toggleExerciseType}
          toggleCondition={toggleCondition}
          onClose={() => setShowAddVideo(false)}
          onSubmit={addVideo}
        />
      )}
      {playingVideoId && (
        <div style={styles.modal} onClick={() => setPlayingVideoId(null)}>
          <div style={{ ...styles.modalContent, maxWidth: '800px' }}>
            <iframe
              width="100%"
              height="400"
              src={`https://www.youtube.com/embed/${playingVideoId}?autoplay=1`}
              title="YouTube video player"
              frameBorder="0"
              allow="autoplay; encrypted-media"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      )}
    </div>
  );
};

// PERFORMANCE FIX 5: Memoized VideoCard component to prevent unnecessary re-renders
const VideoCard = React.memo(({ video, isSelected, onToggle, onDelete, getThumbnailUrl, favorites, toggleFavorite, setPlayingVideoId, isAdmin }) => {
  return (
    <div style={styles.videoCard}>
      <div
        style={styles.videoCardContent}
        onClick={() => onToggle(video.id)}
      >
        <div style={styles.checkboxContainer}>
          {isSelected ? (
            <CheckCircle size={24} style={styles.checkboxChecked} />
          ) : (
            <Circle size={24} style={styles.checkboxUnchecked} />
          )}
        </div>

        <img
          src={getThumbnailUrl(video.videoId)}
          alt={video.title}
          style={styles.thumbnail}
          loading="lazy"
          onClick={(e) => {
            e.stopPropagation();
            setPlayingVideoId(video.videoId);
          }}
          onError={(e) => {
            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="120" height="90" fill="%23e5e7eb"%3E%3Crect width="120" height="90"/%3E%3C/svg%3E';
          }}
        />

        <div style={styles.videoInfo}>
          <h3 style={styles.videoTitle}>{video.title}</h3>
          <div style={styles.videoMeta}>
            <span style={styles.metaTag}>{video.bodyPart}</span>
            {video.exerciseTypes?.map(type => (
              <span key={type} style={styles.metaTagType}>{type}</span>
            ))}
            <span style={styles.metaTagEquipment}>{video.equipment}</span>
            <span style={styles.metaTagDifficulty}>{video.difficulty}</span>
          </div>
          {video.condition && (
            <p style={styles.conditionText}>
              <strong>Condition:</strong> {video.condition}
            </p>
          )}
          {video.otherConditions?.length > 0 && (
            <p style={styles.conditionText}>
              <strong>Also for:</strong> {video.otherConditions.join(', ')}
            </p>
          )}

        </div>
      </div>
      {isAdmin && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(video.id);
          }}
          style={styles.deleteButton}
        >
          <Trash2 size={18} />
        </button>
      )}

      <button
        onClick={(e) => {
          e.stopPropagation();
          toggleFavorite(video.id);
        }}
        style={styles.favoriteButton}
        aria-label={favorites.includes(video.id) ? 'Remove from favourites' : 'Add to favourites'}
        title={favorites.includes(video.id) ? 'Remove from favourites' : 'Add to favourites'}
      >
        {favorites.includes(video.id) ? '⭐' : '☆'}
      </button>
    </div>
  );
});

// Simplified AddVideoModal component
const AddVideoModal = ({
  newVideo,
  setNewVideo,
  bodyParts,
  exerciseTypes,
  equipmentOptions,
  difficultyLevels,
  conditionOptions,
  toggleExerciseType,
  toggleCondition,
  onClose,
  onSubmit
}) => {
  return (
    <div style={styles.modal}>
      <div style={styles.modalContent}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Add New Exercise</h2>
          <button onClick={onClose} style={styles.closeButton}>×</button>
        </div>

        <div style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>YouTube Video ID or URL *</label>
            <input
              type="text"
              placeholder="dQw4w9WgXcQ or https://youtube.com/watch?v=dQw4w9WgXcQ"
              value={newVideo.videoId}
              onChange={(e) => setNewVideo({ ...newVideo, videoId: e.target.value })}
              style={styles.input}
            />
            <p style={styles.helperText}>
              Enter either the 11-character video ID or the full YouTube URL
            </p>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Exercise Title *</label>
            <input
              type="text"
              placeholder="e.g., Shoulder Mobility Exercise"
              value={newVideo.title}
              onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })}
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Body Part *</label>
            <select
              value={newVideo.bodyPart}
              onChange={(e) => setNewVideo({ ...newVideo, bodyPart: e.target.value })}
              style={styles.input}
            >
              <option value="">Select body part</option>
              {bodyParts.map(part => (
                <option key={part} value={part}>{part}</option>
              ))}
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Exercise Types * (select multiple)</label>
            <div style={styles.multiSelectContainer}>
              {exerciseTypes.map(type => (
                <button
                  key={type}
                  type="button"
                  onClick={() => toggleExerciseType(type)}
                  style={{
                    ...styles.multiSelectButton,
                    ...(newVideo.exerciseTypes.includes(type) ? styles.multiSelectButtonActive : {})
                  }}
                >
                  {newVideo.exerciseTypes.includes(type) && (
                    <CheckCircle size={16} style={{ marginRight: '6px' }} />
                  )}
                  {type}
                </button>
              ))}
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Equipment *</label>
            <select
              value={newVideo.equipment}
              onChange={(e) => setNewVideo({ ...newVideo, equipment: e.target.value })}
              style={styles.input}
            >
              <option value="">Select equipment</option>
              {equipmentOptions.map(eq => (
                <option key={eq} value={eq}>{eq}</option>
              ))}
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Difficulty Level *</label>
            <select
              value={newVideo.difficulty}
              onChange={(e) => setNewVideo({ ...newVideo, difficulty: e.target.value })}
              style={styles.input}
            >
              <option value="">Select difficulty</option>
              {difficultyLevels.map(level => (
                <option key={level} value={level}>{level}</option>
              ))}
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Primary Condition *</label>
            <select
              value={newVideo.condition}
              onChange={(e) => setNewVideo({ ...newVideo, condition: e.target.value })}
              style={styles.input}
            >
              <option value="">Select condition</option>
              {conditionOptions.map(cond => (
                <option key={cond} value={cond}>{cond}</option>
              ))}
            </select>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Other Conditions (optional)</label>
            <div style={styles.multiSelectContainer}>
              {conditionOptions.filter(c => c !== newVideo.condition).slice(0, 5).map(cond => (
                <button
                  key={cond}
                  type="button"
                  onClick={() => toggleCondition(cond)}
                  style={{
                    ...styles.multiSelectButton,
                    ...(newVideo.otherConditions.includes(cond) ? styles.multiSelectButtonActive : {})
                  }}
                >
                  {newVideo.otherConditions.includes(cond) && (
                    <CheckCircle size={16} style={{ marginRight: '6px' }} />
                  )}
                  {cond}
                </button>
              ))}
            </div>
            <p style={styles.helperText}>Showing top 5 conditions</p>
            <div style={styles.checkboxGroup}>
              <input
                type="checkbox"
                id="withAccessory"
                checked={newVideo.withAccessory}
                onChange={(e) => setNewVideo({ ...newVideo, withAccessory: e.target.checked })}
                style={styles.checkbox}
              />
              <label htmlFor="withAccessory" style={styles.checkboxLabel}>
                Requires Accessory
              </label>
            </div>
          </div>

          <div style={styles.modalActions}>
            <button onClick={onClose} style={styles.cancelButton}>Cancel</button>
            <button onClick={onSubmit} style={styles.submitButton}>Add Exercise</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    gap: '20px',
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '4px solid #e2e8f0',
    borderTop: '4px solid #0ea5e9',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    color: '#64748b',
    fontSize: '16px',
  },
  loginContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: '20px',
  },
  logoSection: {
    textAlign: 'center',
    marginBottom: '40px',
  },
  logo: {
    width: '80px',
    height: '80px',
    backgroundColor: '#0ea5e9',
    color: 'white',
    borderRadius: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    boxShadow: '0 10px 30px rgba(14, 165, 233, 0.3)',
  },
  appTitle: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 8px 0',
  },
  appSubtitle: {
    fontSize: '16px',
    color: '#64748b',
    margin: 0,
  },
  loginCard: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '32px',
    maxWidth: '400px',
    width: '100%',
    boxShadow: '0 4px 6px rgba(0,0,0,0.07), 0 10px 20px rgba(0,0,0,0.05)',
  },
  loginTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#1e293b',
    margin: '0 0 12px 0',
    textAlign: 'center',
  },
  loginDescription: {
    fontSize: '14px',
    color: '#64748b',
    textAlign: 'center',
    margin: '0 0 32px 0',
    lineHeight: '1.6',
  },
  googleButton: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '14px 24px',
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  googleIcon: {
    width: '20px',
    height: '20px',
  },
  authNote: {
    fontSize: '12px',
    color: '#94a3b8',
    textAlign: 'center',
    margin: '20px 0 0 0',
    lineHeight: '1.5',
  },
  authError: {
    fontSize: '13px',
    color: '#b91c1c',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '10px',
    padding: '12px',
    margin: '0 0 16px 0',
    lineHeight: '1.5',
  },
  authDebugPanel: {
    marginBottom: '16px',
    padding: '12px',
    borderRadius: '10px',
    backgroundColor: '#0f172a',
    color: '#e2e8f0',
    border: '1px solid #1e293b',
  },
  authDebugHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
    fontSize: '12px',
  },
  authDebugClearButton: {
    backgroundColor: '#1e293b',
    color: '#e2e8f0',
    border: '1px solid #334155',
    borderRadius: '6px',
    fontSize: '11px',
    padding: '4px 8px',
    cursor: 'pointer',
  },
  authDebugPre: {
    margin: 0,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontSize: '11px',
    lineHeight: '1.5',
    maxHeight: '220px',
    overflowY: 'auto',
    fontFamily: 'Consolas, Monaco, monospace',
  },
  header: {
    backgroundColor: 'white',
    padding: '20px',
    borderBottom: '1px solid #e2e8f0',
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  headerTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0,
  },
  headerSubtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0,
  },
  videoCount: {
    color: '#94a3b8',
    fontWeight: '400',
  },
  iconButton: {
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f1f5f9',
    border: 'none',
    borderRadius: '10px',
    color: '#475569',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  searchContainer: {
    display: 'flex',
    gap: '12px',
    padding: '16px 20px',
    backgroundColor: 'white',
  },
  navigationMenu: {
    display: 'flex',
    gap: '10px',
    padding: '12px 20px 0 20px',
    backgroundColor: 'white',
    overflowX: 'auto',
  },
  navigationButton: {
    padding: '10px 14px',
    fontSize: '14px',
    fontWeight: '600',
    backgroundColor: '#e2e8f0',
    color: '#475569',
    border: 'none',
    borderRadius: '999px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'all 0.2s',
  },
  navigationButtonActive: {
    backgroundColor: '#0ea5e9',
    color: 'white',
    boxShadow: '0 6px 16px rgba(14, 165, 233, 0.22)',
  },
  searchBar: {
    flex: 1,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  searchIcon: {
    position: 'absolute',
    left: '14px',
    color: '#94a3b8',
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    padding: '12px 40px 12px 46px',
    fontSize: '15px',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    outline: 'none',
    transition: 'all 0.2s',
  },
  clearSearchButton: {
    position: 'absolute',
    right: '12px',
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#e2e8f0',
    border: 'none',
    borderRadius: '50%',
    fontSize: '18px',
    color: '#64748b',
    cursor: 'pointer',
    lineHeight: 1,
  },
  searchingIndicator: {
    padding: '8px 20px',
    backgroundColor: '#f0f9ff',
    borderBottom: '1px solid #e0f2fe',
    fontSize: '13px',
    color: '#0284c7',
    fontStyle: 'italic',
  },
  filterButton: {
    width: '48px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f1f5f9',
    border: 'none',
    borderRadius: '10px',
    color: '#475569',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  filterButtonActive: {
    backgroundColor: '#0ea5e9',
    color: 'white',
  },
  filtersContainer: {
    padding: '16px 20px',
    backgroundColor: 'white',
    borderTop: '1px solid #e2e8f0',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  filterLabel: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#475569',
  },
  select: {
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    backgroundColor: 'white',
    color: '#1e293b',
    outline: 'none',
  },
  clearButton: {
    padding: '10px',
    fontSize: '14px',
    fontWeight: '600',
    backgroundColor: '#f1f5f9',
    border: 'none',
    borderRadius: '8px',
    color: '#475569',
    cursor: 'pointer',
  },
  actionBar: {
    display: 'flex',
    gap: '12px',
    padding: '16px 20px',
    backgroundColor: '#f8fafc',
  },
  addButtonSmall: {
    width: '48px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#0ea5e9',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    flexShrink: 0,
  },
  uploadButton: {
    width: '48px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#8b5cf6',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    flexShrink: 0,
  },
  fileInput: {
    display: 'none',
  },
  shareButton: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px',
    fontSize: '15px',
    fontWeight: '600',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  shareButtonDisabled: {
    backgroundColor: '#94a3b8',
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  videoList: {
    padding: '0 20px 100px 20px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  emptyText: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#475569',
    margin: '0 0 8px 0',
  },
  emptySubtext: {
    fontSize: '14px',
    color: '#94a3b8',
    margin: 0,
  },
  videoCard: {
    display: 'flex',
    alignItems: 'flex-start',
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '12px',
    marginBottom: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    gap: '12px',
  },
  videoCardContent: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    flex: 1,
    cursor: 'pointer',
  },
  checkboxContainer: {
    flexShrink: 0,
    paddingTop: '4px',
  },
  checkboxChecked: {
    color: '#0ea5e9',
  },
  checkboxUnchecked: {
    color: '#cbd5e1',
  },
  thumbnail: {
    width: '90px',
    height: '68px',
    objectFit: 'cover',
    borderRadius: '8px',
    backgroundColor: '#e5e7eb',
    flexShrink: 0,
  },
  videoInfo: {
    flex: 1,
    minWidth: 0,
  },
  videoTitle: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#1e293b',
    margin: '0 0 8px 0',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  videoMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginBottom: '6px',
  },
  metaTag: {
    fontSize: '11px',
    padding: '3px 8px',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    borderRadius: '4px',
    fontWeight: '500',
  },
  metaTagType: {
    fontSize: '11px',
    padding: '3px 8px',
    backgroundColor: '#e0f2fe',
    color: '#0284c7',
    borderRadius: '4px',
    fontWeight: '500',
  },
  metaTagEquipment: {
    fontSize: '11px',
    padding: '3px 8px',
    backgroundColor: '#fef3c7',
    color: '#d97706',
    borderRadius: '4px',
    fontWeight: '500',
  },
  metaTagDifficulty: {
    fontSize: '11px',
    padding: '3px 8px',
    backgroundColor: '#ddd6fe',
    color: '#7c3aed',
    borderRadius: '4px',
    fontWeight: '500',
  },
  conditionText: {
    fontSize: '12px',
    color: '#64748b',
    margin: '4px 0 0 0',
    lineHeight: '1.4',
  },
  deleteButton: {
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    flexShrink: 0,
  },
  favoriteButton: {
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fef3c7',
    color: '#d97706',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    flexShrink: 0,
    fontSize: '18px',
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    zIndex: 1000,
    overflowY: 'auto',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 20px 16px 20px',
    borderBottom: '1px solid #e2e8f0',
    position: 'sticky',
    top: 0,
    backgroundColor: 'white',
    zIndex: 1,
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0,
  },
  closeButton: {
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '28px',
    color: '#64748b',
    cursor: 'pointer',
    lineHeight: 1,
  },
  form: {
    padding: '20px',
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#475569',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '12px',
    fontSize: '15px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  helperText: {
    fontSize: '12px',
    color: '#94a3b8',
    margin: '6px 0 0 0',
    lineHeight: '1.4',
  },
  multiSelectContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '8px',
  },
  multiSelectButton: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 14px',
    fontSize: '13px',
    fontWeight: '500',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    border: '2px solid #e2e8f0',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  multiSelectButtonActive: {
    backgroundColor: '#dbeafe',
    color: '#0284c7',
    borderColor: '#0284c7',
  },
  modalActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px',
    position: 'sticky',
    bottom: 0,
    backgroundColor: 'white',
    paddingTop: '16px',
    borderTop: '1px solid #e2e8f0',
  },
  cancelButton: {
    flex: 1,
    padding: '12px',
    fontSize: '15px',
    fontWeight: '600',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  submitButton: {
    flex: 1,
    padding: '12px',
    fontSize: '15px',
    fontWeight: '600',
    backgroundColor: '#0ea5e9',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  selectedTypesContainer: {
    marginTop: '12px',
    padding: '12px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
  },
  selectedTypesLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#64748b',
    margin: '0 0 8px 0',
  },
  selectedTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  selectedTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    fontSize: '13px',
    fontWeight: '500',
    backgroundColor: '#0ea5e9',
    color: 'white',
    borderRadius: '6px',
  },
  removeTagButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2px',
    backgroundColor: 'transparent',
    border: 'none',
    color: 'white',
    cursor: 'pointer',
    borderRadius: '50%',
    transition: 'all 0.2s',
  }
};

// Add animation keyframes
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  button:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }
  
  button:active:not(:disabled) {
    transform: translateY(0);
  }
  
  input:focus, select:focus {
    border-color: #0ea5e9;
  }
`;
document.head.appendChild(styleSheet);

export default PhysioVideoApp;
